import React, { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { buildComponent } from 'src/components/factory'
import { useResponsive } from 'src/hooks/use-responsive'
import { useStoreState, useStore } from 'src/hooks/state'
import { usePendingRedirect } from 'src/hooks/use-pending-redirect'

import * as UI from 'native-base'
import * as Elements from 'src/components/elements'
import * as Semantics from 'src/components/semantics'
import * as Components from './components'

import {
  setBackgroundMessageHandler,
  getToken,
  requestPermission,
  onNotificationOpenedApp,
  onMessage,
  getInitialNotification,
} from 'src/helpers/messaging'
import {
  createChannel,
  displayNotification,
  onForegroundEvent,
  setBadgeCount,
  EventType,
  AndroidImportance,
  AndroidStyle,
} from 'src/helpers/notifee'
import { PermissionsAndroid } from 'src/helpers/android-permissions'
import { handlerNotification } from 'src/screens/disconnected/login/login'
import { graphql } from 'src/services/graphql'
import { useNavigation } from '@react-navigation/native'

const MainView = buildComponent()
  .withLifecycle(() => {
    const { isDesktop } = useResponsive()
    const insets = useSafeAreaInsets()
    const [gongAnnouncementOpen, setGongAnnouncementOpen] = useState(false)
    const [hasCheckedAnnouncement, setHasCheckedAnnouncement] = useState(false)

    // Check if user needs to see the gong announcement
    const Store = useStore()
    const preferences = useStoreState((store) => store.profile.preferences)
    const user = useStoreState((store) => store.profile.user)
    const availableGongs = useStoreState((store) => store.audio.gongs)

    // Show announcement if:
    // 1. User is logged in
    // 2. Gongs are available
    // 3. User hasn't seen the announcement yet
    // 4. User is onboarded (to avoid showing multiple popups at once)
    // 5. We haven't already checked and shown the announcement
    React.useEffect(() => {
      const showAnnouncementAndSetDefaultGong = async () => {
        if (
          !hasCheckedAnnouncement &&
          user &&
          availableGongs.length > 0 &&
          preferences?.onboarded &&
          !preferences?.gongAnnouncementShown
        ) {
          // Assign the first available gong as default when showing announcement for the first time
          try {
            const defaultGong = availableGongs[0]
            await graphql.mutations.setGong(defaultGong._id)

            // Update user state with the assigned gong
            Store.getActions().profile.updateUser({
              gong: defaultGong,
            })

            console.log('Default gong assigned:', defaultGong.slug)
          } catch (error) {
            console.error('Error assigning default gong:', error)
            // Continue showing announcement even if gong assignment fails
          }

          setGongAnnouncementOpen(true)
          setHasCheckedAnnouncement(true)
        }
      }

      showAnnouncementAndSetDefaultGong()
    }, [user, availableGongs, preferences, hasCheckedAnnouncement, Store])

    const handleCloseAnnouncement = useCallback(() => {
      setGongAnnouncementOpen(false)
    }, [])

    // Sur iOS, le layout.tsx gère déjà le safeAreaTop, donc on n'ajoute pas de padding ici
    // Sur Android, on applique le paddingTop pour gérer la safe area
    const paddingTop = Platform.OS === 'ios' ? 0 : insets.top

    return {
      isDesktop,
      gongAnnouncementOpen,
      handleCloseAnnouncement,
      paddingTop,
    }
  })
  .withRender(({ lifecycle }) => {
    const MainWrapper = (
      lifecycle.isDesktop ? UI.Box : UI.ScrollView
    ) as typeof UI.ScrollView
    const SecondaryWrapper = (
      lifecycle.isDesktop ? UI.ScrollView : UI.Box
    ) as typeof UI.ScrollView

    const navigation = useNavigation()

    const getFcmToken = useCallback(async () => {
      const firebaseToken = await getToken()
      if (firebaseToken) {
        console.log('firebaseToken', firebaseToken)

        const { userUpdateFcmToken } =
          await graphql.mutations.userUpdateFcmToken(firebaseToken)

        console.log('FcmToken -> ', userUpdateFcmToken.success)
        return firebaseToken
      }
    }, [])

    // Android 13+ n'affiche aucune notification tant que POST_NOTIFICATIONS n'a pas
    // été demandée, et Firebase Messaging ne la demande pas lui-même.
    const requestAndroidPermission = async () =>
      Number(Platform.Version) < 33 ||
      (await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS,
      )) === PermissionsAndroid.RESULTS.GRANTED

    const requestUserPermission = useCallback(async () => {
      let authorizationStatus: boolean | number = true
      if (Platform.OS === 'ios') {
        authorizationStatus = await requestPermission()
      }
      if (Platform.OS === 'android') {
        authorizationStatus = await requestAndroidPermission()
      }
      if (authorizationStatus) {
        const myToken = await getFcmToken()
        console.log('myToken ->', myToken)
      } else {
        console.log('pas inscrit')
      }
    }, [getFcmToken])

    React.useEffect(() => {
      ;(async () => {
        await requestUserPermission()
      })()
      console.log('setBackgroundMessageHandler')
      setBackgroundMessageHandler(() => Promise.resolve())
      onForegroundEvent(({ type, detail }) => {
        switch (type) {
          case EventType.PRESS:
            handlerNotification(detail.notification?.data, navigation)
            console.log('test', JSON.stringify(detail, null, 2))
            setBadgeCount(0)
            break
        }
      })
      onNotificationOpenedApp((_remote) => {
        handlerNotification(_remote?.data, navigation)
        setBadgeCount(0)
      })
      onMessage(async (remoteMessage) => {
        console.log(remoteMessage)
        const channelId = await createChannel({
          id: 'channel_nap&up',
          name: 'channel_nap&up',
          vibration: true,
          importance: AndroidImportance.HIGH,
        })
        await displayNotification({
          android: {
            channelId: channelId,
            color: '#FFFFFF',
            style: {
              text: remoteMessage?.notification?.body,
              type: AndroidStyle.BIGTEXT,
            },
          },
          body: remoteMessage?.notification?.body,
          title: remoteMessage?.notification?.title,
          data: remoteMessage?.data,
        })
      })
      getInitialNotification()?.then((_remote) => {
        handlerNotification(_remote?.data, navigation)
        setBadgeCount(0)
      })
    }, [requestUserPermission, navigation])

    React.useEffect(() => {
      const fetchLastOpen = async () => {
        const { userUpdateAppLastOpen } =
          await graphql.mutations.userUpdateAppLastOpen()
        console.log('LastOpen ->', userUpdateAppLastOpen.success)
      }

      fetchLastOpen()
    })

    return (
      <>
        <MainWrapper
          scrollIndicatorInsets={{ right: 1 }}
          flex={1}
          padding={[3, 0]}
          paddingTop={[3 + lifecycle.paddingTop, 0]}
          marginBottom={[88, 0]}
        >
          <Components.Wave />

          <SecondaryWrapper paddingTop={[0, 16]} paddingBottom={[0, 92]}>
            <UI.Box px={[0, 8, 8, 16, 32]} width="100%">
              <Components.Gamification />

              <Elements.NapList active="upcoming" />
            </UI.Box>
          </SecondaryWrapper>

          <Elements.MyAccountButton />
        </MainWrapper>

        <Components.Tabs />

        <Elements.ConnectedOnboarding />

        <Semantics.GongAnnouncement
          isOpen={lifecycle.gongAnnouncementOpen}
          onClose={lifecycle.handleCloseAnnouncement}
        />
      </>
    )
  })

export const ScreenConnectedHome = buildComponent()
  .withLifecycle(() => {
    const loading = usePendingRedirect(
      useStoreState((store) => store.space.bookingDisabled),
      '/category',
      {},
      true,
    )

    return { loading }
  })
  .withRender(({ lifecycle }) => (lifecycle.loading ? <MainView /> : null))
